import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import StylishNewSelect from 'components/DesignSystems/New/StylishNewSelect';
import { Controller, useForm } from 'react-hook-form';
import { assignRole } from 'actions/roleActions';
import { endLoading, startLoading } from 'reducers/loading/loading.action';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import { changeGroupAdminNameToOrgAdmin } from './RolesUtils/groupToOrgTransformations';
import { selectGroup, useAppSelector } from 'slices/commonSelectors';
import { useAors } from 'hooks/useAors';

// Scope type options for role assignment - group and incident only
const SCOPE_TYPE_OPTIONS = [
  { value: 'group', label: 'Organization' },
  { value: 'aor', label: 'Area of Responsibility' },
];

export default function NewRoleAssignmentDialog({
  show,
  onClose,
  group_guid,
  assignee_guid,
}) {
  const reduxDispatch = useDispatch();
  const { data: aors = [] } = useAors();
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ mode: 'onChange' });

  const reduxRostersForGroups = useSelector((state) => {
    const rostersForGroups = state.app.rostersForGroups || [];
    return rostersForGroups.filter(
      (member) => member.group_guid === group_guid
    );
  });

  const reduxAllOrgScopedRolesInGroup = useSelector((state) => {
    return state.app.allOrgScopedRolesInGroup || [];
  });

  // Get current group for scope selection
  const currentGroup = useAppSelector(selectGroup);

  // Watch the selected scope type
  const selectedScopeType = watch('scopeType');
  const defaultScopeType = SCOPE_TYPE_OPTIONS[0];

  // Derive scope entity options based on selected scope type
  const scopeEntityOptions = useMemo(() => {
    const scopeType = selectedScopeType?.value || 'group';
    switch (scopeType) {
      case 'aor':
        return aors.map((aor) => ({
          value: aor.item_id,
          label: aor.name,
        }));
      case 'group':
      default:
        return currentGroup
          ? [{ value: currentGroup.group_guid, label: currentGroup.group_name }]
          : [];
    }
  }, [selectedScopeType, aors, currentGroup]);

  // Default scope entity for organization scope
  const defaultScopeEntity =
    scopeEntityOptions.length > 0 ? scopeEntityOptions[0] : null;

  // Show all roles - same role can be assigned with different scopes
  // Note: Backend DB constraint on (role_id, group_member_guid) currently prevents
  // true multi-scope assignments. A future migration to include scope_id in the
  // constraint will enable this fully. For now, backend silently ignores duplicates.
  const filteredRoles = reduxAllOrgScopedRolesInGroup;

  useEffect(() => {
    if (assignee_guid) {
      const member = reduxRostersForGroups.find(
        (m) => m.user_guid === assignee_guid
      );
      if (member) {
        setValue('groupMember', member.email_address);
      }
    }
  }, [reduxRostersForGroups, assignee_guid, setValue]);

  // Auto-select first scope entity when options change (for non-group scopes)
  useEffect(() => {
    const scopeType = selectedScopeType?.value || 'group';
    if (scopeType !== 'group' && scopeEntityOptions.length > 0) {
      setValue('scopeEntity', scopeEntityOptions[0]);
    }
  }, [scopeEntityOptions, selectedScopeType, setValue]);

  const onNewRoleAssignmentSubmit = async (data) => {
    const scopeType = data.scopeType?.value || 'group';
    const scopeItemId =
      scopeType === 'group' ? group_guid : data.scopeEntity?.value;

    // Validate scope_item_id for non-group scopes
    if (scopeType !== 'group' && !scopeItemId) {
      return;
    }

    const assignedRole = {
      user_guid: assignee_guid,
      role_id: data.availableRoles.value,
      group_guid: group_guid,
      scope_type: scopeType,
      scope_item_id: scopeItemId,
    };

    reduxDispatch(startLoading());
    await reduxDispatch(assignRole(assignedRole));
    reduxDispatch(endLoading());
    onClose();
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        centered
        backdrop="static"
        keyboard={false}
      >
        <form onSubmit={handleSubmit(onNewRoleAssignmentSubmit)}>
          <Modal.Header closeButton closeVariant="white">
            <Modal.Title>New Role</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <label className="form-label">Group Member:</label>
              <Controller
                control={control}
                name="groupMember"
                disabled="true"
                rules={{ required: false }}
                render={({ field: { value } }) => (
                  <StylishNewInput
                    readOnly
                    disabled
                    type="text"
                    value={value}
                  />
                )}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Role:</label>
              <Controller
                control={control}
                name="availableRoles"
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <StylishNewSelect
                    options={filteredRoles.map((role) => {
                      return {
                        value: role.id,
                        label: changeGroupAdminNameToOrgAdmin(role.name),
                      };
                    })}
                    defaultValue={value}
                    value={value || ''}
                    onChange={(e) => onChange(e)}
                    placeholder={'Assign a Role'}
                    isClearable={true}
                    isSearchable={true}
                    isMulti={false}
                    isDisabled={false}
                    noOptionsMessage={() => 'All available roles are assigned.'}
                  />
                )}
              />
              {errors.availableRoles && (
                <span className="form-text form-error">
                  This field is required
                </span>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Scope Type:</label>
              <Controller
                control={control}
                name="scopeType"
                defaultValue={defaultScopeType}
                render={({ field: { onChange, value } }) => (
                  <StylishNewSelect
                    options={SCOPE_TYPE_OPTIONS}
                    value={value || defaultScopeType}
                    onChange={(e) => {
                      onChange(e);
                      // Reset scope entity when scope type changes - will be set by useEffect below
                      setValue('scopeEntity', null);
                    }}
                  />
                )}
              />
            </div>
            <div className="m-0">
              <label className="form-label">Scope:</label>
              <Controller
                control={control}
                name="scopeEntity"
                defaultValue={defaultScopeEntity}
                rules={{
                  required: (selectedScopeType?.value || 'group') !== 'group',
                }}
                render={({ field: { onChange, value } }) => (
                  <StylishNewSelect
                    options={scopeEntityOptions}
                    value={value || defaultScopeEntity}
                    onChange={(e) => onChange(e)}
                    isDisabled={
                      (selectedScopeType?.value || 'group') === 'group'
                    }
                    placeholder="Select scope..."
                    isClearable={true}
                    isSearchable={true}
                    noOptionsMessage={() =>
                      `No ${selectedScopeType?.label || 'items'} available.`
                    }
                  />
                )}
              />
              {errors.scopeEntity && (
                <span className="form-text form-error">
                  Please select a scope
                </span>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="button-group">
              <StylishNewButton
                className="button--secondary button--reverse"
                onClick={onClose}
              >
                Cancel
              </StylishNewButton>
              <StylishNewButton type="submit" className="button--primary">
                Save
              </StylishNewButton>
            </div>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
}
